import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import config from "../config";

const API_CONTACT = config.TUUTUUM_BACKEND_ADMIN_API + "/contact";

const ContactForm = () => {
  const { t } = useTranslation(["contactform", "translation"]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    privacyPolicy: false,
    website: "", // honeypot field — bots fill this, humans never see it
  });
  const [formStatus, setFormStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [formLoadedAt] = useState(() => Date.now()); // timestamp for bot detection

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.privacyPolicy) {
      setFormStatus(t("form_status.privacy_not_accepted"));
      return;
    }

    if (!validateEmail(formData.email)) {
      setFormStatus(t("form_status.invalid_email"));
      return;
    }

    if (formData.message.trim().length < 10) {
      setFormStatus(t("form_status.message_too_short"));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_CONTACT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "cors",
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          website: formData.website, // honeypot
          _t: formLoadedAt, // timestamp for bot detection
        }),
      });

      if (response.ok) {
        setFormStatus(t("form_status.success"));
        setFormData({ name: "", email: "", message: "", privacyPolicy: false, website: "" });
      } else if (response.status === 429) {
        setFormStatus(t("form_status.rate_limited"));
      } else {
        setFormStatus(t("form_status.error"));
      }
    } catch (error) {
      console.error("Contact form error:", error);
      setFormStatus(t("form_status.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col bg-white shadow-lg rounded-lg p-10">
      <h3 className="text-3xl font-bold text-center mb-3">
        {t("contact_form.title")}
      </h3>
      <p className="text-center mb-3">{t("contact_form.description")}</p>

      {formStatus && (
        <div className="bg-green-100 text-green-800 p-4 rounded mb-6 text-center">
          {formStatus}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            {t("form_field.name")}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
            placeholder={t("form_field.your_name")}
          />
        </div>

        {/* E-mail */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            {t("form_field.email")}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
            placeholder={t("form_field.your_email")}
          />
        </div>

        {/* Message */}
        <div>
          <label
            htmlFor="message"
            className="block text-sm font-medium text-gray-700"
          >
            {t("form_field.message")}
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows="4"
            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
            placeholder={t("form_field.your_message")}
          ></textarea>
        </div>

        {/* Privacy Policy Checkbox */}
        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="privacyPolicy"
              checked={formData.privacyPolicy}
              onChange={handleChange}
              required
              className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">
              <Trans
                i18nKey="form_field.privacy_policy"
                components={{
                  a: (
                    <a
                      href={`/terms#${t(
                        "hashes.privacy",
                        { ns: "translation" }
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              >
                {t("form_field.privacy_policy")}
              </Trans>
            </span>
          </label>
        </div>

        {/* Honeypot — hidden from humans, bots will fill it */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: "-9999px",
            opacity: 0,
            height: 0,
            overflow: "hidden",
            tabIndex: -1,
          }}
        >
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            value={formData.website}
            onChange={handleChange}
            autoComplete="off"
            tabIndex={-1}
          />
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={!formData.privacyPolicy || loading}
            className={`w-full btn-main ${
              !formData.privacyPolicy || loading
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            {loading ? t("form_field.loading") : t("form_field.submit")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
